import { useState, FormEvent, useRef, useEffect } from 'react';

import './LoginForm.scss';
import { DDRumConfig, RedirectUrl } from '@/config';
import { Link } from '@cwlib/react-lib/lib/Link';
import { LinkPrompt } from '@cwlib/react-lib/lib/LinkPrompt';
import { TextInput } from '@cwlib/react-lib/lib/TextInput';
import { Button } from '@cwlib/react-lib/lib/Button';
import { default as Form } from 'react-bootstrap/Form';
import { default as Alert } from 'react-bootstrap/Alert';
import StatusAlert from '@/components/StatusAlert';
import { LoginService } from '@/services';
import { Credentials, MfaType } from '@/components/LoginVerification';
import { faPencil, faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Cookie from '@/utils/cookie';
import { REDIRECT_COOKIE, REDIRECT_PARAM } from '@/routes/Login/Login';
import { useLocation, useSearchParams } from 'react-router-dom';
import { updateAuthUserInfoToRum } from '@/datadogRum';
import { hasResetPasswordFlow, RESET_PASSWORD_FLOW, RESET_PASSWORD_FLOW_PARAM } from '@/utils/passwordResetEntry';

type LoginFormProps = {
  onVerification?: (
    mfaType: MfaType,
    msg: string,
    credentials: Credentials
  ) => void;
  onLoginSuccess?: (redirectUrl: string) => void;
  xeroEmail?: string;
  initialEmail?: string;
  isPasswordResetFlow?: boolean;
  isPasswordResetSuccess?: boolean;
  isPasswordResetLinkInvalid?: boolean;
  isNewUser?: boolean;
  // Send password users to Auth0 Universal Login (ACUL) when enabled by the login route.
  isUniversalLogin?: boolean;
  centeredLayout?: boolean;
};

type ResetAPIV2Error = {
  status: number;
  title?: string;
  detail?: string;
  id?: string;
};

type ResetAPIV2ErrorResponse = {
  jsonapi?: Record<string, unknown>;
  errors: ResetAPIV2Error[];
};

export const ERROR_PARAM = 'error';
export const SUCCESS_PARAM = 'success';
const ERROR_DESCRIPTION_PARAM = 'error_description';
const AUTH0_RATE_LIMIT_ERROR_MESSAGE = 'There are too many sign-in requests right now. Please wait a few minutes and try again.';
const GENERIC_AUTH0_ERROR_MESSAGE = 'We couldn’t complete your sign-in. Please try again. If the issue continues, contact support.';

/*
  Federation Flow: default state for login page; 
  Aim is to determine if the user is federated and run through the external auth or default sign in
*/
type FederatedFlow = {
  isFederated: boolean;
  loginWithPassword?: boolean;
  ssoHyperlink?: string;
  shouldHideContinueWithPassword: boolean;
};
const DEFAULT_FEDERATED_FLOW: FederatedFlow = {
  isFederated: true,
  loginWithPassword: false,
  ssoHyperlink: '',
  shouldHideContinueWithPassword: false
};
const LoginForm = ({ onVerification, onLoginSuccess, xeroEmail, initialEmail, isPasswordResetFlow = false, isPasswordResetSuccess, isPasswordResetLinkInvalid, isNewUser = false, isUniversalLogin = false, centeredLayout = false }: LoginFormProps) => {
  const [loading, setLoading] = useState(false);
  const [isPasskeyRedirecting, setIsPasskeyRedirecting] = useState(false);
  const [email, setEmail] = useState(initialEmail ?? xeroEmail ?? '');
  const [password, setPassword] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isPasskeyExperimentRequested =
    searchParams.get('passkey') === 'true' &&
    !searchParams.has(ERROR_PARAM);
  const [message, setMessage] = useState(() => {
    if (isUniversalLogin && searchParams.has(ERROR_PARAM)) {
      // shortcut: recognizes Auth0's English rate-limit wording; replace when a stable error code or other locales are supported.
      const description = searchParams.get(ERROR_DESCRIPTION_PARAM) || '';
      return /rate[- ]limit|too many requests/i.test(description)
        ? AUTH0_RATE_LIMIT_ERROR_MESSAGE
        : GENERIC_AUTH0_ERROR_MESSAGE;
    }
    return searchParams.get(ERROR_PARAM) || searchParams.get(SUCCESS_PARAM) || '';
  });
  const [isSuccessMessage, setIsSuccessMessage] = useState(!!searchParams.get(SUCCESS_PARAM));
  const [showPasswordResetForm, setShowPasswordResetForm] = useState(isPasswordResetFlow || isPasswordResetLinkInvalid);
  const [federatedFlow, setFederationFlow] = useState<FederatedFlow>(DEFAULT_FEDERATED_FLOW);
  const [resetButtonDisabled, setResetButtonDisabled] = useState(false);
  const [resetCooldownTime, setResetCooldownTime] = useState(0);
  const [isPasswordSubmissionStep, setIsPasswordSubmissionStep] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Set while a federation check or login URL request is in flight. A ref (not
  // state) so a second click in the same tick is ignored: each /login-url call
  // rotates the cw_sso_* cookies, so overlapping requests would break the callback.
  const isPasswordStepPending = useRef(false);
  const passkeyRedirectStarted = useRef(false);

  const shouldShowResetToggleLink = (!federatedFlow.isFederated || federatedFlow.loginWithPassword) || showPasswordResetForm;

  /**
   * Removes message query parameter from the URL
   * Purpose: When user submits the form, previous params are invalid.
   */
  const removeMessageQueryParams = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("message");
    setSearchParams(newSearchParams, { replace: true });
  };

  const getResetToggleParams = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('message');
    if (!showPasswordResetForm) params.set(RESET_PASSWORD_FLOW_PARAM, RESET_PASSWORD_FLOW);
    else if (hasResetPasswordFlow(params)) params.delete(RESET_PASSWORD_FLOW_PARAM);
    return params;
  };

  // Text content helpers based on form state
  const getPageHeading = () => {
    if (showPasswordResetForm) {
      return 'Reset your password';
    }
    return 'Sign in';
  };

  const getSignupPromptMessage = () => {
    return "Don't have a CreditorWatch account yet?";
  };

  const renderResetToggleLink = () => {
    const linkTitle = showPasswordResetForm ? 'Back to sign in' : 'Forgot Password?';
    const query = getResetToggleParams().toString();
    const href = `${location.pathname}${query ? `?${query}` : ''}`;

    return (
      <div className="reset-toggle-link new-flow">
        {showPasswordResetForm && <FontAwesomeIcon icon={faArrowLeft} className="reset-arrow-icon" />}
        <Link title={linkTitle} href={href} onClick={toggleShowPasswordResetForm} />
      </div>
    );
  };

  const getButtonTitle = () => {
    if (showPasswordResetForm) {
      const shouldShowCooldown = resetButtonDisabled && resetCooldownTime > 0;
      return shouldShowCooldown
        ? `Send reset link (${resetCooldownTime}s)`
        : 'Send reset link';
    }

    if (federatedFlow.isFederated && !federatedFlow.ssoHyperlink) return 'Next';
    if (federatedFlow.isFederated && federatedFlow.ssoHyperlink && !federatedFlow.loginWithPassword) return 'Sign in with SSO';
    return 'Sign In';
  };

  const isButtonLoading = loading && !showPasswordResetForm && !isPasskeyRedirecting;
  const isContinueWithPasswordLoading = isButtonLoading && !!federatedFlow.ssoHyperlink && !federatedFlow.loginWithPassword;
  const shouldShowResetLinkSentAlert = showPasswordResetForm && resetButtonDisabled && resetCooldownTime > 0;

  const getEmailFieldDisabled = () => {
    return !showPasswordResetForm && isPasswordSubmissionStep;
  };

  const shouldShowEditButton = () => {
    if (!email?.length) return false;
    return !showPasswordResetForm && isPasswordSubmissionStep;
  };

  const clearResetInterval = () => {
    if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
  }

  const handleNewResetApiError = (errorData: ResetAPIV2ErrorResponse | undefined): string | null => {
    if (!errorData?.errors?.length) {
      return null;
    }
    const firstError: ResetAPIV2Error = errorData.errors[0];
    return firstError.detail || 'An error occurred, please try again.';
  };

  // Cleanup timer on unmount and when component updates
  useEffect(() => {
    return () => {
      clearResetInterval();
    };
  }, []);

  /**
   * Releases the in-flight password step (busy CTA) so the form is usable again.
   *
   * @returns void
   * @throws never
   */
  const releasePasswordStep = () => {
    isPasswordStepPending.current = false;
    setLoading(false);
    setIsPasskeyRedirecting(false);
  };

  // Pressing Back from Auth0 can restore this page from the back-forward cache
  // with the CTA still busy from the redirect; release it so the user can retry.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) releasePasswordStep();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const goToPasskeyLogin = () => {
    if (!isUniversalLogin || showPasswordResetForm || isPasswordStepPending.current) return;

    setMessage('');
    setIsSuccessMessage(false);
    isPasswordStepPending.current = true;
    setLoading(true);
    setIsPasskeyRedirecting(true);

    // Omitting email avoids login_hint, which would skip login-id and land on login-password.
    LoginService.getLoginUrl()
      .then((loginUrl) => {
        const passkeyUrl = new URL(loginUrl);
        passkeyUrl.searchParams.set('ext-passkey', '1');
        window.location.assign(passkeyUrl.toString());
      })
      .catch((err) => {
        console.error('Auth0 Universal Login passkey URL request failed', {
          status: err?.status
        });
        releasePasswordStep();
        setMessage(
          'Unable to start passkey sign-in. Please try again or use email and password.'
        );
      });
  };

  useEffect(() => {
    if (
      !isPasskeyExperimentRequested ||
      !isUniversalLogin ||
      passkeyRedirectStarted.current
    ) return;

    passkeyRedirectStarted.current = true;
    goToPasskeyLogin();
  }, [isPasskeyExperimentRequested, isUniversalLogin, showPasswordResetForm]);

  // Start 30-second cooldown timer
  const startResetCooldown = () => {
    // Clear any existing timer first
    clearResetInterval();

    // Set initial countdown time immediately to avoid showing 0s
    setResetCooldownTime(30);

    timerRef.current = setInterval(() => {
      setResetCooldownTime((prev) => {
        if (prev <= 1) {
          setResetButtonDisabled(false);
          clearResetInterval();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const toggleShowPasswordResetForm = (event?: React.MouseEvent<HTMLAnchorElement>) => {
    if (isPasskeyRedirecting) {
      event?.preventDefault();
      return;
    }
    // Keyboard-generated clicks can have button=-1; only auxiliary/right clicks
    // and modifier clicks should follow the native href without the state update.
    if (event && (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
    event?.preventDefault();
    const params = getResetToggleParams();
    setShowPasswordResetForm(!showPasswordResetForm);
    setMessage('');
    setIsSuccessMessage(false);
    setPassword('');
    // A real href supports keyboard/new-tab navigation; regular clicks keep the
    // editable email in memory and update reset mode without a page reload.
    setSearchParams(params, { replace: true });
  };

  const onEditEmailAction = () => {
    if (isPasskeyRedirecting) return;
    // User can return from federated, non-federated or reset password flow
    // Clear password, error messages, sso link, and enable federated flow while keeping the email as is
    setPassword('');
    setShowPasswordResetForm(false);
    setMessage('');
    setFederationFlow(DEFAULT_FEDERATED_FLOW);
    setIsPasswordSubmissionStep(false); // Reset the "Next" state to allow email editing
    releasePasswordStep();
  };

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const onPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const onLoginFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isPasswordStepPending.current) return;
    if (!email.length || (!password.length && !showPasswordResetForm)) {
      return;
    }
    // Prevent submission if reset button is disabled
    if (showPasswordResetForm && resetButtonDisabled) {
      return;
    }
    setMessage('');
    setIsSuccessMessage(false);
    showPasswordResetForm ? resetPasswordSubmit() : signInFormSubmit();
  };

  const signInFormSubmit = () => {
    setLoading(true);

    LoginService.loginByBffUser(email, password)
      .then(async ({ data }) => {
        if (DDRumConfig.isEnabled) {
          await updateAuthUserInfoToRum();
        }

        if (data.redirect) {
          onLoginSuccess(data.redirect);
          return;
        } else if (data.detail) {
          const {deliveryMethod, statusMessage, status} = data.detail;
          if (status === 'ACTIVE') {
            onVerification(deliveryMethod, statusMessage, { email, password });
            return;
          }
          if (statusMessage) {
            setMessage(statusMessage);
            return;
          }
        }
      })
      .catch((err) => {
        const errData = err?.data;

        if (errData?.detail?.statusMessage) {
          setMessage(errData.detail.statusMessage);
          return;
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const resetPasswordSubmit = () => {
    setLoading(true);
    // Disable the reset button immediately
    setResetButtonDisabled(true);

    LoginService.forgotPasswordNewFlow(email)
      .then((res) => {
        if (res && res.data && res.data.error) {
          setMessage(res.data.error || "There was an error, please try again.");
          setResetButtonDisabled(false);
          return;
        }
        if (res && res.data && res.data.message) {
          setIsSuccessMessage(true);
          startResetCooldown();
        } else {
          setMessage('There was an error with your request, please try again or contact our customer support team.');
          setResetButtonDisabled(false);
        }
      })
      .catch((err) => {
        const jsonApiError = handleNewResetApiError(err?.data);
        setMessage(jsonApiError);
        setResetButtonDisabled(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /**
   * Moves the user to the password step. With isUniversalLogin enabled, the
   * browser is sent to the ACUL login_url; otherwise the inline password
   * field is shown.
   *
   * @param inlinePasswordFlow - federation state that reveals the inline
   *   password field.
   * @returns void; navigation happens as a side effect.
   * @throws never — a failed login_url request (network/4xx/5xx/invalid URL)
   *   is logged via console.error (forwarded to Datadog RUM when enabled) and
   *   falls back to the inline password field.
   */
  const goToPasswordStep = (inlinePasswordFlow: FederatedFlow) => {
    if (!isUniversalLogin) {
      releasePasswordStep();
      setFederationFlow(inlinePasswordFlow);
      return;
    }

    // Loading stays on after success: the page is navigating away.
    isPasswordStepPending.current = true;
    setLoading(true);
    LoginService.getLoginUrl(email)
      .then((loginUrl) => window.location.assign(loginUrl))
      .catch((err) => {
        // Never lock the user out: fall back to the inline password field.
        // Log only the HTTP status: the rejected axios response carries the email
        // in config.params, and console.error is forwarded to Datadog RUM.
        console.error('Auth0 Universal Login URL request failed', { status: err?.status });
        releasePasswordStep();
        setFederationFlow(inlinePasswordFlow);
      });
  };

  /**
   * Checks whether the email belongs to a federated (SSO) user and moves the
   * form to the matching next step: the SSO choice for federated users, else
   * the password step (ACUL redirect or inline field, see goToPasswordStep).
   *
   * @param email - the email the user entered.
   * @returns void; state updates happen as side effects.
   * @throws never — a failed federation check is treated as non-federated.
   */
  const checkIfUserIsFederated = (email: string) => {
    if (isUniversalLogin) {
      isPasswordStepPending.current = true;
      setLoading(true);
    }
    setIsPasswordSubmissionStep(true);

    LoginService.checkIfUserIsFederated(email)
      .then(
        ({
          data: {
            data: { is_federated: isFederated, federation_link = null },
            shouldHideContinueWithPassword
          }
        }) => {
          if (isFederated) {
            const newFederationFlow: FederatedFlow = {
              ...federatedFlow,
              isFederated: true,
              ssoHyperlink: federation_link,
              shouldHideContinueWithPassword
            };

            if (!federatedFlow.isFederated) {
              newFederationFlow.loginWithPassword = false;
            }

            releasePasswordStep();
            setFederationFlow(newFederationFlow);
          } else goToPasswordStep({ ...DEFAULT_FEDERATED_FLOW, isFederated: false });
        }
      )
      .catch((_err) => {
        goToPasswordStep({ ...DEFAULT_FEDERATED_FLOW, isFederated: false });
      })
      .finally(() => {
        setMessage('');
      });
  };

  const pageHeading = (
    <Form.Group className="cw-login-form-group cw-login-form-group-title">
      <h1 className='new-flow-heading'>{getPageHeading()}</h1>
      {showPasswordResetForm ? (
        <p className="description-text">
          We'll send you a link to reset your password.
        </p>
      ) : (
        <LinkPrompt
          style="info"
          linkRef={RedirectUrl.pricing}
          promptMessage={getSignupPromptMessage()}
          linkTitle="Sign up"
        />
      )}
    </Form.Group>
  );

  return (
    <>
      {centeredLayout && (
        <div className="login-centered-header cw-login-form new-flow">
          {pageHeading}
        </div>
      )}
      <Form
        className={`new-flow cw-login-form${
          centeredLayout ? ' login-centered-card' : ''
        }`}
        onSubmit={async (e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          if (isPasswordStepPending.current) return;

          const shouldHandleFederatedFlow = !showPasswordResetForm && !isPasswordResetLinkInvalid && (federatedFlow.isFederated && !federatedFlow.loginWithPassword);

          if (shouldHandleFederatedFlow) {
            if (federatedFlow.ssoHyperlink) {
              const redirectCookie = Cookie.get(REDIRECT_COOKIE);
              if (!redirectCookie) {
                const redirectUrl = searchParams.get(REDIRECT_PARAM);
                redirectUrl?.length > 0 && Cookie.set(REDIRECT_COOKIE, redirectUrl);
              }
              window.location.replace(federatedFlow.ssoHyperlink);
            } else {
              if (email.length > 0) checkIfUserIsFederated(email);
              else setMessage('Please enter email address');
            }
          } else onLoginFormSubmit(e);
          // Query param 'message' is invalid after form submit
          removeMessageQueryParams();
        }}
        noValidate
      >
        {message.length ? (
          <Alert
            className="cw-login-form-alert"
            variant={isSuccessMessage ? 'success' : 'danger'}
          >
            {message}
          </Alert>
        ) : null}
        {shouldShowResetLinkSentAlert && (
          <StatusAlert
            type="success"
            title="Check your inbox"
            messages={[
              "Follow the instructions in the email to create a new password.",
              "Didn't get it? Check your spam folder or try again."
            ]}
          />
        )}
        {isPasswordResetSuccess && (
          <StatusAlert
            type="success"
            title="Password update successful"
            messages={["You can sign in with your new password."]}
          />
        )}
        {isPasswordResetLinkInvalid && (
          <StatusAlert
            type="error"
            title={isNewUser ? "Account setup link has expired" : "Reset link has expired"}
            messages={[
              isNewUser
                ? "For your security, account setup links expire after a short time. Please request a new link via the reset password feature to setup your account."
                : "For your security, password reset links expire after a short time. Please request a new link to reset your password."
            ]}
          />
        )}
        {!centeredLayout && pageHeading}
        <Form.Group
          className={`cw-login-form-group cw-login-form-input-group${isPasskeyRedirecting ? ' passkey-redirecting' : ''}`}
        >
          <TextInput
            inputType="email"
            ariaLabel={showPasswordResetForm ? 'Email' : undefined}
            autoComplete="off"
            placeholder="Enter your email"
            value={email}
            onChange={onEmailChange}
            disabled={getEmailFieldDisabled()}
            {...(shouldShowEditButton() && {iconConfig: {
                  right: {
                    icon: faPencil,
                    onIconClickAction: onEditEmailAction
                  }
            }})}
            {...(message.length && { state: 'error' })}
          />
          {!showPasswordResetForm && (!federatedFlow.isFederated || federatedFlow.loginWithPassword) && (
              <TextInput
                inputType="password"
                placeholder="Password"
                autoComplete="off"
                value={password}
                onChange={onPasswordChange}
                {...(message.length && { state: 'error' })}
              />
            )}
        </Form.Group>

        <Form.Group className="cw-login-form-group signin-links">
          <div className='cw-login-form-btns-group'>
            <Button title={getButtonTitle()}
              type="submit"
              loading={isButtonLoading && !isContinueWithPasswordLoading}
              isCTA={true}
              active={
                !(showPasswordResetForm && (loading || resetButtonDisabled)) &&
                !isPasskeyRedirecting && !isContinueWithPasswordLoading
              }
            />
          </div>
          {shouldShowResetToggleLink && !isPasskeyRedirecting && renderResetToggleLink()}
          {federatedFlow.isFederated && federatedFlow.ssoHyperlink && !federatedFlow.loginWithPassword && !federatedFlow.shouldHideContinueWithPassword && <>
              <span className='divider'>
                <span className='divider-line'></span>
                <span className='divider-text'>or</span>
                <span className='divider-line'></span>
                </span>
              <Button title="Continue with password"
                type="button"
                variant='inverse'
                loading={isContinueWithPasswordLoading}
                active={!loading}
                onClick={() => {
                  if (isPasswordStepPending.current) return;
                  goToPasswordStep({ ...federatedFlow, loginWithPassword: true });
                }}
                isCTA={true} />
              {isUniversalLogin && !showPasswordResetForm && (
                <Button
                  title="Sign in with a passkey"
                  type="button"
                  variant="inverse"
                  className="passkey-login-button"
                  loading={isPasskeyRedirecting}
                  active={!loading}
                  isCTA={true}
                  onClick={goToPasskeyLogin}
                />
              )}
            </>}
        </Form.Group>
      </Form>
    </>
  );
};

export default LoginForm;
